import { useNavigate }    from 'react-router-dom';
import { useRoleAccess }  from '../../users/hooks/useRoleAccess';
import { useRequests }    from '../hooks/useRequests';
import { Plus }           from 'lucide-react';
import {
  useState,
  useMemo,
  useCallback,
  useDeferredValue
} from 'react';

import {
  SearchBar,
  SortableHeader,
  StatusFilters,
  Pagination,
  LoadingState,
  ErrorState,
  EmptyState,
  RequestRow,
  RequestWithRelations
} from '@cleanpro/common-ui';

/**
 * Requests component for managing service requests in the admin panel.
 * 
 * This component provides a comprehensive interface for viewing, searching, filtering,
 * and managing service requests. It includes features for sorting, pagination,
 * and status-based filtering to help administrators efficiently manage the request workflow.
 * 
 * @component
 * 
 * @features
 * - **Search**: Real-time search across customer name, email, and service name
 * - **Filtering**: Status-based filtering (all, assessed, pending, quoted, expired, cancelled)
 * - **Sorting**: Sortable columns for customer, property, contact, status, requested time, and created date
 * - **Pagination**: Paginated view with 25 items per page
 * - **Navigation**: Click-to-view individual requests and create new requests
 * - **Permissions**: Role-based access control for creating/editing requests
 * - **State Management**: Loading and error states with retry functionality
 * 
 * @example
 * ```tsx
 * // Used in the admin routing system
 * <Route path="/requests" element={<Requests />} />
 * ```
 * 
 * @returns JSX element containing the complete requests management interface
 * 
 * @dependencies
 * - `useRoleAccess` - For permission checking
 * - `useNavigate` - For routing navigation
 * - `useRequests` - For request data management
 * - `RequestRow` - For individual request display
 * - `SearchBar` - For search functionality
 * - `StatusFilters` - For status filtering
 * - `Pagination` - For paginated navigation
 * - `SortableHeader` - For sortable table headers
 * - `LoadingState` - For loading display
 * - `ErrorState` - For error handling
 * - `EmptyState` - For empty state display
 */
export default function Requests() {
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const { requests, loading, error, refreshRequests } = useRequests();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const ITEMS_PER_PAGE = 25;

  const { filteredRequests, filterCounts } = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();
    const counts: Record<'all' | 'assessed' | 'pending' | 'quoted' | 'expired' | 'cancelled', number> = {
      all: 0,
      assessed: 0,
      pending: 0,
      quoted: 0,
      expired: 0,
      cancelled: 0,
    };

    const matches = requests.filter((request: RequestWithRelations) => {
      const haystacks = [
        request.customer?.name ?? '',
        request.customer?.email ?? '',
        request.service?.name ?? ''
      ];

      const matchesSearch =
        normalizedSearch.length === 0 ||
        haystacks.some((value) => value.toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) {
        return false;
      }

      counts.all += 1;
      if (counts[request.status as keyof typeof counts] !== undefined) {
        counts[request.status as keyof typeof counts] += 1;
      }

      return true;
    });

    const filtered = matches.filter((request) =>
      statusFilter === 'all' || request.status === statusFilter
    );

    return { filteredRequests: filtered, filterCounts: counts };
  }, [requests, deferredSearchTerm, statusFilter]);

  const sortedRequests = useMemo(() => {
    return [...filteredRequests].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'customer':
          aValue = a.customer?.name ?? '';
          bValue = b.customer?.name ?? '';
          break;
        case 'property':
          aValue = a.property?.addressLine1 ?? '';
          bValue = b.property?.addressLine1 ?? '';
          break;
        case 'contact':
            aValue = a.customer?.email ?? '';
            bValue = b.customer?.email ?? '';
            break;
        case 'requested':
          aValue = a.requestedTime ? new Date(a.requestedTime).getTime() : 0;
          bValue = b.requestedTime ? new Date(b.requestedTime).getTime() : 0;
          break;
        case 'created':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'status':
          aValue = a.status ?? '';
          bValue = b.status ?? '';
          break;
        default: // createdAt
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredRequests, sortColumn, sortDirection]);

  const totalRequests = sortedRequests.length;
  const totalPages = Math.ceil(totalRequests / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentRequests = sortedRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Request status filters based on workflow insights
  const REQUEST_FILTERS = useMemo(() => [
    { 
      value: 'all', 
      label: 'All Requests', 
      count: filterCounts.all 
    },
    { 
      value: 'assessed', 
      label: 'Assessed', 
      count: filterCounts.assessed 
    },
    { 
      value: 'pending', 
      label: 'Pending', 
      count: filterCounts.pending 
    },
    { 
      value: 'quoted', 
      label: 'Quoted', 
      count: filterCounts.quoted 
    },
    { 
      value: 'expired', 
      label: 'Expired', 
      count: filterCounts.expired 
    },
    { 
      value: 'cancelled', 
      label: 'Cancelled', 
      count: filterCounts.cancelled 
    }
  ], [filterCounts]);

  // Memoized event handlers for better performance
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  const handleRequestClick = useCallback((request: RequestWithRelations) => {
    console.log(request);
    navigate(`/requests/${request.id}`);
  }, [navigate]);

  const handleCreateRequest = useCallback(() => {
    navigate('/requests/create');
  }, [navigate]);

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  // Handle loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <LoadingState message="Loading requests..." size="lg" />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="space-y-6">
        <ErrorState 
          error={error} 
          onRetry={refreshRequests} 
          showRetry={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Requests</h1>
          <p className="text-gray-600 mt-1">Requests need to be reviewed, then quoted, and then associated with a job</p>
        </div>
        {permissions.canEditJobs && (
          <button
            onClick={handleCreateRequest}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-indigo-700 transition-colors min-h-[44px] whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            <span>New Request</span>
          </button>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search requests by name, email, phone, or address..."
          />

          {/* Request Type Filters */}
          <StatusFilters
            filters={REQUEST_FILTERS}
            selectedFilter={statusFilter}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {currentRequests.length === 0 ? (
          <EmptyState
            title="No requests found"
            description={
              searchTerm || statusFilter !== 'all'
                ? "No requests match your current filters. Try adjusting your search or filter criteria."
                : "No service requests have been created yet."
            }
            actionLabel={permissions.canEditJobs && !searchTerm && statusFilter === 'all' ? "Create First Request" : undefined}
            onAction={permissions.canEditJobs && !searchTerm && statusFilter === 'all' ? handleCreateRequest : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader 
                      label="Customer"
                      column="customer"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Property"
                      column="property"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Contact"
                      column="contact"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Status"
                      column="status"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Requested"
                      column="requested"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Created"
                      column="created"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentRequests.map((request) => (
                    <RequestRow
                      key={request.id}
                      request={request}
                      onClick={handleRequestClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalRequests}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
